import { useRef, useState, useEffect, useCallback } from 'react';

const NEAR_BOTTOM_THRESHOLD = 50; // px

/**
 * Manages auto-scroll for chat containers during streaming.
 * Pauses auto-scroll when the user scrolls up; resumes when they
 * return to the bottom or click the "scroll to bottom" button.
 *
 * @param {object}  opts
 * @param {boolean} opts.isStreaming  — true while a response is streaming
 * @param {Array}   opts.deps        — additional deps that trigger auto-scroll
 *                                      (e.g. [streamingText, messages.length])
 * @returns {{ containerRef, bottomRef, showScrollBtn, scrollToBottom }}
 */
export function useChatScroll({ isStreaming, deps = [] }) {
  const [containerEl, setContainerEl] = useState(null);
  const bottomRef = useRef(null);
  const userScrolledUp = useRef(false);
  const prevIsStreaming = useRef(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  // Callback ref — captures the DOM element when mounted, even if conditional
  const containerRef = useCallback((node) => {
    setContainerEl(node);
  }, []);

  // Reset scroll lock when a new streaming session starts (user sent a message)
  useEffect(() => {
    if (isStreaming && !prevIsStreaming.current) {
      userScrolledUp.current = false;
      setShowScrollBtn(false);
    }
    prevIsStreaming.current = isStreaming;
  }, [isStreaming]);

  // Track programmatic scrolls to avoid the scroll handler re-enabling the button
  const isProgScroll = useRef(false);

  // Scroll handler — detect whether user has scrolled away from bottom
  useEffect(() => {
    if (!containerEl) return;

    const onScroll = () => {
      // Skip handler during programmatic scrolls (scrollToBottom)
      if (isProgScroll.current) return;

      const distFromBottom = containerEl.scrollHeight - containerEl.scrollTop - containerEl.clientHeight;
      const nearBottom = distFromBottom < NEAR_BOTTOM_THRESHOLD;
      userScrolledUp.current = !nearBottom;
      setShowScrollBtn(!nearBottom);
    };

    containerEl.addEventListener('scroll', onScroll, { passive: true });
    return () => containerEl.removeEventListener('scroll', onScroll);
  }, [containerEl]);

  // Auto-scroll effect — only scrolls when user hasn't scrolled up
  useEffect(() => {
    if (!userScrolledUp.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps]);

  const scrollToBottom = useCallback(() => {
    userScrolledUp.current = false;
    isProgScroll.current = true;
    setShowScrollBtn(false);
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Re-enable scroll handler after animation settles
    setTimeout(() => { isProgScroll.current = false; }, 500);
  }, []);

  return { containerRef, bottomRef, showScrollBtn, scrollToBottom };
}
